import React, { useEffect, useState } from "react";
import { api, LEAD_STATUSES, SERVICES, CITIES, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Eye, ArrowRightCircle } from "lucide-react";
import { RIVIERA_CITIES } from "@/lib/riviera";
import { CountryBadge } from "@/components/admin/CountryBadge";

const STATUS_BADGE_COLOR = {
  "New Lead": "bg-blue-100 text-blue-800 ring-blue-200",
  "Contacted": "bg-indigo-100 text-indigo-800 ring-indigo-200",
  "Quoted": "bg-amber-100 text-amber-800 ring-amber-200",
  "Awaiting Customer Confirmation": "bg-orange-100 text-orange-800 ring-orange-200",
  "Confirmed": "bg-emerald-100 text-emerald-800 ring-emerald-200",
  "Lost": "bg-slate-100 text-slate-600 ring-slate-200",
  "Cancelled": "bg-rose-100 text-rose-700 ring-rose-200",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState({ country: "", status: "", city: "", service_type: "" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const q = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => v && q.append(k, v));
    const r = await api.get(`/leads?${q}`);
    setLeads(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const convert = async (id) => {
    try {
      await api.post(`/leads/${id}/convert`);
      toast.success("Converted to customer & job");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const changeStatus = async (lead, newStatus) => {
    if (!newStatus || newStatus === lead.status) return;
    // Optimistic update
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: newStatus } : l)));
    try {
      await api.patch(`/leads/${lead.id}`, { status: newStatus });
      toast.success(`Lead set to "${newStatus}".`);
    } catch (e) {
      toast.error(formatError(e));
      load();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">All quote and booking requests.</p>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-4 gap-3 max-w-4xl">
        <Select value={filter.country || "all"} onValueChange={(v) => setFilter({ ...filter, country: v === "all" ? "" : v, city: "" })}>
          <SelectTrigger data-testid="lead-filter-country"><SelectValue placeholder="All countries" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="lead-filter-country-all">All countries</SelectItem>
            <SelectItem value="NL" data-testid="lead-filter-country-nl">Netherlands (NL)</SelectItem>
            <SelectItem value="FR" data-testid="lead-filter-country-fr">France / Riviera (FR)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="lead-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.city} onValueChange={(v) => setFilter({ ...filter, city: v === "all" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All cities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {(filter.country === "FR" ? RIVIERA_CITIES.map((c) => c.name) : filter.country === "NL" ? CITIES : [...CITIES, ...RIVIERA_CITIES.map((c) => c.name)]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.service_type} onValueChange={(v) => setFilter({ ...filter, service_type: v === "all" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All services" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All services</SelectItem>
            {[...SERVICES, "Villa / Yacht-Season Turnover"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Country</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Quote</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell><CountryBadge country={l.country} testid={`lead-country-${l.id}`} /></TableCell>
                <TableCell>
                  <div className="font-medium">{l.customer_name}</div>
                  <div className="text-xs text-slate-500">{l.phone}</div>
                </TableCell>
                <TableCell>{l.service_type}</TableCell>
                <TableCell>{l.city}</TableCell>
                <TableCell>{l.preferred_date || "—"}</TableCell>
                <TableCell>
                  <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                    <SelectTrigger
                      data-testid={`lead-status-select-${l.id}`}
                      className={`h-8 w-[195px] text-xs font-medium border-0 ring-1 rounded-full px-3 ${STATUS_BADGE_COLOR[l.status] || "bg-slate-100 text-slate-700 ring-slate-200"}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>{l.quoted_price ? `€${l.quoted_price}` : "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(l)} data-testid={`lead-view-${l.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => convert(l.id)} data-testid={`lead-convert-${l.id}`} title="Convert to customer & job">
                    <ArrowRightCircle className="w-4 h-4 text-[#5BA83F]" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {leads.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No leads found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <LeadDetailDialog lead={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function LeadDetailDialog({ lead, onClose, onSaved }) {
  const [form, setForm] = useState({ status: "", quoted_price: "", internal_notes: "" });
  useEffect(() => {
    if (lead) setForm({ status: lead.status || "", quoted_price: lead.quoted_price ?? "", internal_notes: lead.internal_notes || "" });
  }, [lead]);

  if (!lead) return null;
  const save = async () => {
    try {
      await api.patch(`/leads/${lead.id}`, {
        status: form.status,
        quoted_price: form.quoted_price === "" ? null : parseFloat(form.quoted_price),
        internal_notes: form.internal_notes,
      });
      toast.success("Lead updated");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{lead.customer_name}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <Info label="Phone" value={lead.phone} />
          <Info label="Email" value={lead.email} />
          <Info label="Service" value={lead.service_type} />
          <Info label="City" value={lead.city} />
          <Info label="Address" value={lead.address} />
          <Info label="Date / Time" value={`${lead.preferred_date || "—"} ${lead.preferred_time || ""}`} />
          <Info label="Property" value={`${lead.property_size || "?"} · ${lead.bedrooms || 0} bd / ${lead.bathrooms || 0} ba`} />
          <Info label="Frequency" value={lead.frequency} />
          <Info label="Furnished" value={lead.furnished} />
          <Info label="Contact" value={lead.preferred_contact} />
          <Info label="Country" value={lead.country === "FR" ? "France / Riviera (FR) — quote only" : "Netherlands (NL)"} />
          {lead.postal_code && <Info label="Postal code" value={lead.postal_code} />}
          {lead.client_type && <Info label="Client type" value={lead.client_type} />}
          {lead.floors && <Info label="Floors" value={lead.floors} />}
          {lead.addons?.length > 0 && <Info label="Extras" value={lead.addons.join(", ")} />}
        </div>
        {lead.special_requests && <div className="text-sm"><b>Special requests:</b> {lead.special_requests}</div>}
        {lead.access_instructions && <div className="text-sm"><b>Access:</b> {lead.access_instructions}</div>}

        <div className="grid md:grid-cols-2 gap-3 pt-2">
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quoted price (€)</Label>
            <Input type="number" value={form.quoted_price} onChange={(e) => setForm({ ...form, quoted_price: e.target.value })} className="mt-1.5" data-testid="lead-quoted-price" />
          </div>
        </div>
        <div>
          <Label>Internal notes</Label>
          <Textarea value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} className="mt-1.5" />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="lead-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-800">{value || "—"}</div>
    </div>
  );
}
